import React, { useState, useMemo } from "react";
import { fetchWithAuth, setUser as setAuthUser } from "../../auth/auth";
import { getApiUrl } from "../../utils/getApiUrl";
import type { UserModel, UserParamsMap, UserSettingsPanelModel, UserParamEntry, UserParamsSaveMap } from "../../type/user.tsx";
import { useToast } from "../../components/toast/toast.tsx";
import Checkbox from "../../components/checkbox/checkbox.tsx";
import SettingItemSlider from "../../components/settingItemSlider/settingItemSlider.tsx";
import { useLanguage } from "../../context/languageContext";

const UserSettingsPanel: React.FC<UserSettingsPanelModel> = ({
  user,
  settings,
  onSettingsUpdate,
  onUserUpdate
}) => {
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const { showToast } = useToast();
  const { languages, selectedLanguageId, setSelectedLanguageId } = useLanguage();

  // Group settings by their group field and sort by group name
  const groupedSettings = useMemo(() => {
    const groups: Record<string, Array<{ key: string; entry: UserParamEntry }>> = {};
    
    Object.entries(settings).forEach(([key, entry]) => {
      const group = entry.group || "Other";
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push({ key, entry });
    });

    // Sort groups alphabetically
    return Object.keys(groups)
      .sort((a, b) => a.localeCompare(b))
      .map(groupName => ({
        name: groupName,
        items: groups[groupName]
      }));
  }, [settings]);


const handleSettingChange = async (key: string, newValue?: string) => {
  if (!user) return;

  const currentEntry = settings[key];
  if (!currentEntry) return;

  const resolvedValue = newValue !== undefined ? newValue : !currentEntry.value;

  const updatedSettings: UserParamsMap = {
    ...settings,
    [key]: {
      ...currentEntry,
      value: resolvedValue
    }
  };

  const updatedSettingsSave: UserParamsSaveMap = {
    [key]: resolvedValue
  };

  onSettingsUpdate(updatedSettings);
  setSavingKey(key);

  try {
    const response = await fetchWithAuth(getApiUrl("user/update"), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        iduser: user.iduser,
        parameters: JSON.stringify(updatedSettingsSave)
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to update settings");
    }

    const updatedUser: UserModel = {
      ...user,
      parameters: updatedSettings
    };
    onUserUpdate(updatedUser);
    setAuthUser(updatedUser);
    document.dispatchEvent(new CustomEvent('user:settingsUpdated', { detail: updatedUser }));
    //setParameters(updatedSettings);
  } catch (error) {
    console.error("Error updating settings:", error);
    onSettingsUpdate(settings);
    showToast("Failed to update settings. Please try again.", "error");
  } finally {
    setSavingKey(null);
  }
};

  return (
    <div className="user-settings-panel">
      {groupedSettings.map((group) => (
        <div className="user-settings-section" key={group.name}>
          <h3>
            <i className="bi bi-eye-fill me-2"></i>
            {group.name}
          </h3>
          <div className="user-settings-list">
            {group.items.map(({ key, entry }) => (
              <React.Fragment key={key}>
                {key === "dark_mode" && languages.length > 0 && (
                  <div className="setting-toggle-item setting-toggle-item-default">
                    <div className="setting-toggle-item-info">
                      <div className="setting-toggle-item-label">Language</div>
                      <div className="setting-toggle-item-description">Choose a language to learn</div>
                    </div>
                    <select
                      className="language-select"
                      value={selectedLanguageId ?? ""}
                      onChange={(e) => setSelectedLanguageId(Number(e.target.value))}
                    >
                      {languages.map((lang) => (
                        <option key={lang.idlanguage} value={lang.idlanguage}>
                          {lang.native_name || lang.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {entry.paramtype === "slider" ? (
                  <SettingItemSlider
                    label={entry.label}
                    description={entry.description}
                    value={entry.value as string}
                    onChange={(level: string) => handleSettingChange(key, level)}
                    saving={savingKey === key}
                    variant="slider"
                  />
                ) : (
                  <Checkbox
                    label={entry.label}
                    description={entry.description}
                    checked={!!entry.value}
                    onChange={() => handleSettingChange(key)}
                    saving={savingKey === key}
                    variant="default"
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default UserSettingsPanel;